/**
 * Экран «Анонсы» (мероприятия отеля) — tenant-app, боевой тенант «Облака».
 *
 * Дизайн перенесён 1:1 из manager v3 §12 + guest v7 §9:
 *   12.1 список — карточки c обложкой + date-badge + время + локация +
 *        ведущий + цена + RSVP-счётчик + фильтр-чипы (Сегодня/Предстоящие/
 *        Архив) + CTA «+ Событие»;
 *   12.2 создать — форма: тип / название / описание / даты / место / ведущий /
 *        вместимость / цена / обложка (presign /v1/files как #434) +
 *        «Предпросмотр» (гостевая карточка до публикации) + «Сохранить
 *        черновик» / «Опубликовать»;
 *   12.3 редактирование — те же поля + действия (опубликовать/скрыть/удалить).
 *   guest v7 §9 — гостевая карточка: обложка, дата, описание, инфо-строки
 *        (место / длительность / занятость / цена), RSVP.
 *
 * Отдельное «Расписание» (правка Артёма «просто время и текст») — секция
 * внизу: простые строки «время — текст», менеджер добавляет/удаляет, гость
 * только читает. Бэк: /v1/events/schedule (миграция 0066).
 *
 * Роль-роутинг (один поддомен на отель): canManage (менеджер/владелец) —
 * полный CRUD; гость/персонал — read-only афиша.
 *
 * i18n: 100% RU-лейблы. Мобайл 375: одноколоночный stack.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { Event, EventCategory, EventCreateInput } from '@hsc/types';
import { EVENT_CATEGORIES } from '@hsc/types';
import { Toast } from '../../shared/ui/Toast.js';
import { usePhotoUpload } from '../catalog/usePhotoUpload.js';
import {
  EVENT_CATEGORY_LABEL,
  EVENT_STATE_LABEL,
  EVENT_STATE_TONE,
  bucketOf,
  dateBadge,
  formatDateTime,
  formatPrice,
  formatTime,
  isoToLocalInput,
  localInputToISO,
  seatsLabel,
} from './events-format.js';
import {
  useCreateEvent,
  useEventLifecycle,
  useEvents,
  useRsvpStatus,
  useToggleRsvp,
  useUpdateEvent,
} from './useEvents.js';
import { ScheduleSection } from './ScheduleSection.js';

type Bucket = 'today' | 'upcoming' | 'past' | 'all';

const BUCKET_TABS: ReadonlyArray<{ key: Bucket; label: string }> = [
  { key: 'today', label: 'Сегодня' },
  { key: 'upcoming', label: 'Предстоящие' },
  { key: 'past', label: 'Архив' },
  { key: 'all', label: 'Все' },
];

/** Обложка для показа: presigned-GET если есть, иначе RAW cover_url. */
function coverSrc(event: Event): string | undefined {
  const url = event.cover_preview_url ?? event.cover_url;
  return url !== undefined && url !== '' ? url : undefined;
}

export interface EventsPageProps {
  /** Управляющая роль (owner/manager): полный CRUD. Иначе — read-only афиша. */
  canManage?: boolean;
}

export function EventsPage({ canManage = false }: EventsPageProps): JSX.Element {
  const { data, isLoading, isError } = useEvents();
  const [tab, setTab] = useState<Bucket>(canManage ? 'all' : 'upcoming');
  const [editing, setEditing] = useState<Event | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<Event | null>(null);
  const [toast, setToast] = useState<{ msg: string; tone: 'info' | 'error' } | null>(null);

  const events = useMemo(() => data?.events ?? [], [data]);

  const counts = useMemo(() => {
    const now = new Date();
    let today = 0;
    let upcoming = 0;
    let past = 0;
    for (const e of events) {
      const b = bucketOf(e.starts_at, now);
      if (b === 'today') today += 1;
      else if (b === 'upcoming') upcoming += 1;
      else past += 1;
    }
    return { today, upcoming, past, all: events.length };
  }, [events]);

  const filtered = useMemo(() => {
    const now = new Date();
    const list =
      tab === 'all' ? events : events.filter((e) => bucketOf(e.starts_at, now) === tab);
    return [...list].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  }, [events, tab]);

  const notify = (msg: string, tone: 'info' | 'error' = 'info'): void =>
    setToast({ msg, tone });

  if (canManage && (creating || editing)) {
    return (
      <EventForm
        event={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onDone={(msg) => {
          setCreating(false);
          setEditing(null);
          notify(msg);
        }}
        onError={(msg) => notify(msg, 'error')}
      />
    );
  }

  if (!canManage && viewing) {
    return <GuestEventDetail event={viewing} onClose={() => setViewing(null)} onNotify={notify} />;
  }

  return (
    <section data-testid="events-page">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">Анонсы</h1>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCreating(true)}
            data-testid="events-create"
          >
            + Событие
          </Button>
        )}
      </div>
      <p className="ta-muted" data-testid="events-summary">
        {canManage
          ? `Мероприятий: ${counts.all} · сегодня ${counts.today} · предстоящих ${counts.upcoming}`
          : 'Афиша мероприятий отеля «Облака»'}
      </p>

      <div className="fleet-tabs" role="tablist" aria-label="Фильтр мероприятий">
        {BUCKET_TABS.map((t) => {
          const n =
            t.key === 'today'
              ? counts.today
              : t.key === 'upcoming'
                ? counts.upcoming
                : t.key === 'past'
                  ? counts.past
                  : counts.all;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={tab === t.key ? 'fleet-tab is-active' : 'fleet-tab'}
              onClick={() => setTab(t.key)}
              data-testid={`events-tab-${t.key}`}
            >
              {t.label} · {n}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="fleet-list" data-testid="events-skeleton">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} height={96} radius={16} />
          ))}
        </div>
      ) : isError ? (
        <Card>
          <p role="alert" data-testid="events-error">
            Не удалось загрузить мероприятия. Попробуйте обновить страницу.
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <p data-testid="events-empty">
            {canManage
              ? 'Анонсов пока нет. Создайте первое мероприятие — гости увидят его в Telegram.'
              : 'Пока нет запланированных мероприятий. Загляните позже.'}
          </p>
        </Card>
      ) : (
        <ul className="fleet-list" data-testid="events-list">
          {filtered.map((e) => (
            <EventCard
              key={e.id}
              event={e}
              canManage={canManage}
              onOpen={() => (canManage ? setEditing(e) : setViewing(e))}
            />
          ))}
        </ul>
      )}

      {/* Отдельное расписание дня (время + текст) — внизу афиши. */}
      <ScheduleSection canManage={canManage} onError={(m) => notify(m, 'error')} onInfo={notify} />

      {toast && <Toast message={toast.msg} tone={toast.tone} onClose={() => setToast(null)} />}
    </section>
  );
}

/**
 * Карточка события по дизайну §12.1: обложка + date-badge + название + время +
 * локация + ведущий + цена + RSVP-счётчик.
 */
function EventCard({
  event,
  canManage,
  onOpen,
}: {
  event: Event;
  canManage: boolean;
  onOpen: () => void;
}): JSX.Element {
  const badge = dateBadge(event.starts_at);
  const tone = EVENT_STATE_TONE[event.state] ?? 'muted';
  const cover = coverSrc(event);
  const metaBits = [
    `${formatTime(event.starts_at)}${event.ends_at ? ` — ${formatTime(event.ends_at)}` : ''}`,
    event.location,
    event.host ? `ведёт ${event.host}` : undefined,
  ].filter((x): x is string => x !== undefined && x !== '');

  return (
    <li>
      <button
        type="button"
        className="events-card events-card--btn events-card--rich"
        onClick={onOpen}
        data-testid={`event-row-${event.id}`}
      >
        {cover ? (
          <span
            className="events-card__cover"
            style={{ backgroundImage: `url(${cover})` }}
            aria-hidden="true"
            data-testid={`event-cover-${event.id}`}
          />
        ) : (
          <span className="events-card__badge" aria-hidden="true">
            <span className="events-card__day">{badge.day}</span>
            <span className="events-card__month">{badge.month}</span>
          </span>
        )}
        <span className="events-card__main">
          <span className="events-card__title">{event.title}</span>
          <span className="events-card__meta">
            {metaBits.join(' · ')} · {EVENT_CATEGORY_LABEL[event.category] ?? event.category}
          </span>
          <span className="events-card__sub">
            {formatPrice(event.price_kopecks, event.price_currency)}
            {event.capacity !== undefined
              ? ` · до ${event.capacity} мест`
              : event.price_kopecks
                ? ''
                : ''}
          </span>
        </span>
        {canManage ? (
          <span className={`fleet-badge fleet-badge--${tone}`}>
            {EVENT_STATE_LABEL[event.state] ?? event.state}
          </span>
        ) : (
          <span className="events-card__chevron" aria-hidden="true">
            ›
          </span>
        )}
      </button>
    </li>
  );
}

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'general' as EventCategory,
  starts_at: '',
  ends_at: '',
  location: '',
  host: '',
  capacity: '',
  price: '',
};

/** Форма создания/редактирования (дизайн §12.2 / §12.3) + предпросмотр. */
function EventForm({
  event,
  onClose,
  onDone,
  onError,
}: {
  event: Event | null;
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const isEdit = event !== null;
  const [form, setForm] = useState(() =>
    event
      ? {
          title: event.title,
          description: event.description,
          category: event.category,
          starts_at: isoToLocalInput(event.starts_at),
          ends_at: isoToLocalInput(event.ends_at),
          location: event.location ?? '',
          host: event.host ?? '',
          capacity: event.capacity !== undefined ? String(event.capacity) : '',
          price:
            event.price_kopecks !== undefined && event.price_kopecks > 0
              ? String(event.price_kopecks / 100)
              : '',
        }
      : { ...EMPTY_FORM },
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hideReason, setHideReason] = useState('');
  const [askHide, setAskHide] = useState(false);
  const [preview, setPreview] = useState(false);
  // §12.2 «RSVP и цена»: RSVP-тогл + режим цены (Бесплатно/Платно) chips.
  const [rsvpOn, setRsvpOn] = useState<boolean>(() =>
    event ? event.capacity !== undefined : true,
  );
  const [paid, setPaid] = useState<boolean>(() =>
    event ? event.price_kopecks !== undefined && event.price_kopecks > 0 : false,
  );
  // §12.2 «Push гостям» (UI-тоглы публикации; бэк шлёт уведомление при publish).
  const [pushNow, setPushNow] = useState(true);
  const [remind2h, setRemind2h] = useState(true);

  // Обложка события (§12 cover photo): presign /v1/files (kind=photo).
  const cover = usePhotoUpload(1);
  const [existingCover, setExistingCover] = useState<{ url: string; preview: string } | null>(
    event && event.cover_url !== undefined && event.cover_url !== ''
      ? { url: event.cover_url, preview: event.cover_preview_url ?? event.cover_url }
      : null,
  );
  const newCover = cover.photos[0];
  const coverPreviewSrc =
    newCover?.previewUrl !== undefined && newCover.previewUrl !== ''
      ? newCover.previewUrl
      : (existingCover?.preview ?? undefined);

  const create = useCreateEvent();
  const update = useUpdateEvent();
  const lifecycle = useEventLifecycle();
  const busy = create.isPending || update.isPending || lifecycle.isPending || cover.isUploading;

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]): void =>
    setForm((f) => ({ ...f, [k]: v }));

  const valid = form.title.trim() !== '' && form.starts_at !== '' && form.ends_at !== '';

  /** Итоговый cover_url для тела запроса (новый key / существующий / пусто). */
  const resolveCoverUrl = (): string => {
    if (cover.uploadedKeys.length > 0) return cover.uploadedKeys[0] ?? '';
    if (existingCover !== null) return existingCover.url;
    return '';
  };

  // Цена в копейках: учитывает режим (Платно/Бесплатно из §12.2 chips).
  const priceKopecks = (): number | null => {
    if (!paid) return null;
    const p = form.price.trim();
    if (p === '') return null;
    const n = Number(p);
    if (Number.isNaN(n) || n < 0) return null;
    return Math.round(n * 100);
  };

  // Вместимость: только когда RSVP включён (§12.2 toggle «RSVP включён»).
  const capacityNum = (): number | null => {
    if (!rsvpOn) return null;
    const c = form.capacity.trim();
    if (c === '') return null;
    const n = Number(c);
    return Number.isNaN(n) || n < 0 ? null : Math.round(n);
  };

  // submit — сохраняет (создаёт черновик / правит). publishAfter=true →
  // публикует сразу (CTA «Опубликовать» из §12.2). pushNow=true при публикации
  // запускает уведомление гостям в TG-канал (бэк: publish → notification).
  const submit = (publishAfter: boolean): void => {
    if (!valid) {
      onError('Заполните название и даты начала/окончания.');
      return;
    }
    if (cover.isUploading) {
      onError('Дождитесь загрузки обложки.');
      return;
    }
    const startsISO = localInputToISO(form.starts_at);
    const endsISO = localInputToISO(form.ends_at);
    const detailFields = {
      cover_url: resolveCoverUrl(),
      location: form.location.trim(),
      host: form.host.trim(),
      capacity: capacityNum(),
      price_kopecks: priceKopecks(),
    };
    const publishOk = (): void =>
      onDone(pushNow ? 'Готово · Уведомление пошло гостям' : 'Событие опубликовано.');
    if (isEdit) {
      update.mutate(
        {
          id: event.id,
          input: {
            title: form.title.trim(),
            description: form.description.trim(),
            starts_at: startsISO,
            ends_at: endsISO,
            ...detailFields,
          },
        },
        {
          onSuccess: () => {
            if (publishAfter && event.state !== 'published') {
              lifecycle.mutate(
                { id: event.id, action: 'publish' },
                { onSuccess: publishOk, onError: (e) => onError(e.message || 'Не удалось опубликовать.') },
              );
            } else {
              onDone('Изменения сохранены.');
            }
          },
          onError: (e) => onError(e.message || 'Не удалось сохранить событие.'),
        },
      );
      return;
    }
    const input: EventCreateInput = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      starts_at: startsISO,
      ends_at: endsISO,
      ...detailFields,
    };
    create.mutate(input, {
      onSuccess: (created) => {
        if (publishAfter) {
          lifecycle.mutate(
            { id: created.id, action: 'publish' },
            { onSuccess: publishOk, onError: (e) => onError(e.message || 'Не удалось опубликовать.') },
          );
        } else {
          onDone('Черновик создан. Опубликуйте, чтобы гости увидели событие.');
        }
      },
      onError: (e) => onError(e.message || 'Не удалось создать событие.'),
    });
  };

  const act = (action: 'publish' | 'hide' | 'delete', reason?: string): void => {
    if (!event) return;
    lifecycle.mutate(
      { id: event.id, action, ...(reason !== undefined ? { reason } : {}) },
      {
        onSuccess: () => {
          const msg =
            action === 'publish'
              ? 'Событие опубликовано — гости увидят его в афише.'
              : action === 'hide'
                ? 'Событие скрыто из афиши.'
                : 'Событие удалено.';
          onDone(msg);
        },
        onError: (e) => onError(e.message || 'Действие не выполнено.'),
      },
    );
  };

  // Предпросмотр гостевой карточки до публикации (дизайн §12.2 «Предпросмотр»).
  if (preview) {
    const draft: Event = {
      ...(event ?? ({} as Event)),
      id: (event?.id ?? 'preview') as Event['id'],
      title: form.title.trim() || 'Без названия',
      description: form.description.trim(),
      category: form.category,
      state: 'draft',
      starts_at: localInputToISO(form.starts_at),
      ends_at: localInputToISO(form.ends_at),
      location: form.location.trim(),
      host: form.host.trim(),
      cover_preview_url: coverPreviewSrc,
      capacity: capacityNum() ?? undefined,
      price_kopecks: priceKopecks() ?? undefined,
      price_currency: 'RUB',
    } as Event;
    return (
      <GuestEventDetail
        event={draft}
        previewMode
        onClose={() => setPreview(false)}
        onNotify={() => undefined}
      />
    );
  }

  return (
    <section className="events-form" data-testid="event-form">
      {/* topbar §12.2: крошки + h2 + CTA «Сохранить черновик» / «Опубликовать». */}
      <div className="ta-topbar events-form__topbar">
        <div>
          <div className="events-form__crumbs" data-testid="event-form-crumbs">
            Анонсы / {isEdit ? 'Редактирование' : 'Новое событие'}
          </div>
          <h1 className="hsc-page__title">{isEdit ? 'Редактирование события' : 'Создать событие'}</h1>
        </div>
        <div className="events-form__topbar-actions">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="event-form-close">
            Назад
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || !valid}
            onClick={() => submit(false)}
            data-testid="event-save-draft"
          >
            {isEdit ? 'Сохранить' : 'Сохранить черновик'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !valid}
            onClick={() => submit(true)}
            data-testid="event-publish-cta"
          >
            Опубликовать
          </Button>
        </div>
      </div>

      {/* grid-2 §12.2: слева «Основное», справа stack (RSVP+цена/Cover/Push). */}
      <div className="events-form__grid">
        <Card>
          <h2 className="events-form__card-title">Основное</h2>
          <div className="ta-field">
            <label htmlFor="ev-cat">Тип</label>
            {isEdit ? (
              <p className="ta-muted">
                {EVENT_CATEGORY_LABEL[form.category] ?? form.category} (тип изменить нельзя)
              </p>
            ) : (
              <div className="events-chips" role="group" aria-labelledby="ev-cat">
                {EVENT_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={form.category === c ? 'fleet-chip is-active' : 'fleet-chip'}
                    aria-pressed={form.category === c}
                    onClick={() => set('category', c)}
                    data-testid={`event-cat-${c}`}
                  >
                    {EVENT_CATEGORY_LABEL[c]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ta-field">
            <label htmlFor="ev-title">Название</label>
            <input
              id="ev-title"
              className="ta-input"
              type="text"
              maxLength={200}
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Йога на закате"
              data-testid="event-title"
            />
          </div>

          <div className="ta-field">
            <label htmlFor="ev-desc">Описание</label>
            <textarea
              id="ev-desc"
              className="ta-textarea"
              rows={4}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Расскажите гостям о мероприятии"
              data-testid="event-desc"
            />
          </div>

          <div className="events-form__dates">
            <div className="ta-field">
              <label htmlFor="ev-start">Начало</label>
              <input
                id="ev-start"
                className="ta-input"
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => set('starts_at', e.target.value)}
                data-testid="event-start"
              />
            </div>
            <div className="ta-field">
              <label htmlFor="ev-end">Окончание</label>
              <input
                id="ev-end"
                className="ta-input"
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => set('ends_at', e.target.value)}
                data-testid="event-end"
              />
            </div>
          </div>

          <div className="ta-field">
            <label htmlFor="ev-loc">Место</label>
            <input
              id="ev-loc"
              className="ta-input"
              type="text"
              maxLength={200}
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="Терраса бассейна"
              data-testid="event-location"
            />
          </div>
          <div className="ta-field">
            <label htmlFor="ev-host">Ведущий</label>
            <input
              id="ev-host"
              className="ta-input"
              type="text"
              maxLength={200}
              value={form.host}
              onChange={(e) => set('host', e.target.value)}
              placeholder="Юля Светлова"
              data-testid="event-host"
            />
          </div>
        </Card>

        <div className="events-form__side">
          {/* §12.2 «RSVP и цена» */}
          <Card>
            <h2 className="events-form__card-title">RSVP и цена</h2>
            <div className="ta-field">
              <span className="ta-field__label">RSVP включён</span>
              <button
                type="button"
                className="events-toggle-row"
                onClick={() => setRsvpOn((v) => !v)}
                aria-pressed={rsvpOn}
                data-testid="event-rsvp-toggle-form"
              >
                <span className={rsvpOn ? 'pf-toggle on' : 'pf-toggle'} aria-hidden="true" />
                <span>Гости могут записаться</span>
              </button>
            </div>
            {rsvpOn && (
              <div className="ta-field">
                <label htmlFor="ev-cap">Вместимость (мест)</label>
                <input
                  id="ev-cap"
                  className="ta-input"
                  type="number"
                  min={0}
                  value={form.capacity}
                  onChange={(e) => set('capacity', e.target.value)}
                  placeholder="20"
                  data-testid="event-capacity"
                />
              </div>
            )}
            <div className="ta-field">
              <span className="ta-field__label">Цена</span>
              <div className="events-chips" role="group" aria-label="Цена">
                <button
                  type="button"
                  className={!paid ? 'fleet-chip is-active' : 'fleet-chip'}
                  aria-pressed={!paid}
                  onClick={() => setPaid(false)}
                  data-testid="event-price-free"
                >
                  Бесплатно
                </button>
                <button
                  type="button"
                  className={paid ? 'fleet-chip is-active' : 'fleet-chip'}
                  aria-pressed={paid}
                  onClick={() => setPaid(true)}
                  data-testid="event-price-paid"
                >
                  Платно
                </button>
              </div>
            </div>
            {paid && (
              <div className="ta-field">
                <label htmlFor="ev-price">Цена, ₽</label>
                <input
                  id="ev-price"
                  className="ta-input"
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.price}
                  onChange={(e) => set('price', e.target.value)}
                  placeholder="3500"
                  data-testid="event-price"
                />
              </div>
            )}
          </Card>

          {/* §12.2 «Cover photo» — загрузка изображения + превью + «Заменить» */}
          <Card>
            <h2 className="events-form__card-title">Cover photo</h2>
            <div className="events-form__cover">
              {coverPreviewSrc !== undefined ? (
                <span
                  className="events-form__cover-preview"
                  style={{ backgroundImage: `url(${coverPreviewSrc})` }}
                  data-testid="event-cover-preview"
                />
              ) : (
                <label htmlFor="ev-cover-input" className="events-form__cover-empty">
                  Нет обложки — нажмите, чтобы загрузить
                </label>
              )}
              <div className="events-form__cover-actions">
                <label htmlFor="ev-cover-input" className="hsc-btn hsc-btn--ghost hsc-btn--sm">
                  {coverPreviewSrc !== undefined ? 'Заменить' : 'Загрузить'}
                </label>
                <input
                  id="ev-cover-input"
                  type="file"
                  accept="image/*"
                  className="events-form__cover-file"
                  onChange={(e) => cover.addFiles(e.target.files)}
                  data-testid="event-cover-input"
                />
                {coverPreviewSrc !== undefined && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (newCover !== undefined) cover.removePhoto(newCover.id);
                      setExistingCover(null);
                    }}
                    data-testid="event-cover-remove"
                  >
                    Убрать
                  </Button>
                )}
              </div>
            </div>
            {cover.isUploading && (
              <p className="ta-muted" data-testid="event-cover-uploading">
                Загрузка обложки…
              </p>
            )}
          </Card>

          {/* §12.2 «Push гостям» */}
          <Card>
            <h2 className="events-form__card-title">Push гостям</h2>
            <button
              type="button"
              className="events-toggle-row"
              onClick={() => setPushNow((v) => !v)}
              aria-pressed={pushNow}
              data-testid="event-push-now"
            >
              <span className={pushNow ? 'pf-toggle on' : 'pf-toggle'} aria-hidden="true" />
              <span>Уведомить TG-канал гостей сразу при публикации</span>
            </button>
            <button
              type="button"
              className="events-toggle-row"
              onClick={() => setRemind2h((v) => !v)}
              aria-pressed={remind2h}
              data-testid="event-push-remind"
            >
              <span className={remind2h ? 'pf-toggle on' : 'pf-toggle'} aria-hidden="true" />
              <span>Напоминание за 2 часа</span>
            </button>
          </Card>
        </div>
      </div>

      {isEdit && (
        <Card>
          <h2 className="hsc-card__title">Состояние и действия</h2>
          <p className="ta-muted" data-testid="event-state">
            Текущее состояние:{' '}
            <span className={`fleet-badge fleet-badge--${EVENT_STATE_TONE[event.state] ?? 'muted'}`}>
              {EVENT_STATE_LABEL[event.state] ?? event.state}
            </span>
            {event.published_at ? ` · опубликовано ${formatDateTime(event.published_at)}` : ''}
          </p>
          {event.hide_reason ? (
            <p className="ta-muted">Причина скрытия: {event.hide_reason}</p>
          ) : null}
          <div className="events-form__actions">
            {(event.state === 'draft' || event.state === 'hidden') && (
              <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={() => act('publish')}
                data-testid="event-publish"
              >
                Опубликовать
              </Button>
            )}
            {event.state === 'published' && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setAskHide(true)}
                data-testid="event-hide"
              >
                Скрыть из афиши
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              data-testid="event-delete"
            >
              Удалить
            </Button>
          </div>

          {askHide && (
            <div className="events-confirm" role="dialog" aria-label="Скрыть событие">
              <p>Укажите причину скрытия (видна только команде):</p>
              <input
                className="ta-input"
                type="text"
                value={hideReason}
                onChange={(e) => setHideReason(e.target.value)}
                placeholder="Например: перенос даты"
                data-testid="event-hide-reason"
              />
              <div className="events-form__actions">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy || hideReason.trim() === ''}
                  onClick={() => act('hide', hideReason.trim())}
                  data-testid="event-hide-confirm"
                >
                  Скрыть
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setAskHide(false)}>
                  Отмена
                </Button>
              </div>
            </div>
          )}

          {confirmDelete && (
            <div className="events-confirm" role="dialog" aria-label="Удалить событие">
              <p>Удалить событие «{event.title}»? Это действие необратимо.</p>
              <div className="events-form__actions">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => act('delete')}
                  data-testid="event-delete-confirm"
                >
                  Удалить
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* sticky publish снизу (особенно для 375 — §12.2 «stacked + sticky publish»). */}
      <div className="events-form__submit">
        <Button
          variant="ghost"
          disabled={busy || form.title.trim() === ''}
          onClick={() => setPreview(true)}
          data-testid="event-preview"
        >
          Предпросмотр
        </Button>
        <Button
          variant="secondary"
          disabled={busy || !valid}
          onClick={() => submit(false)}
          data-testid="event-submit"
        >
          {isEdit ? 'Сохранить' : 'Сохранить черновик'}
        </Button>
        <Button
          variant="primary"
          disabled={busy || !valid}
          onClick={() => submit(true)}
          data-testid="event-submit-publish"
        >
          Опубликовать
        </Button>
      </div>
    </section>
  );
}

/**
 * Гостевая карточка события + RSVP (дизайн guest v7 §9). Header-обложка,
 * название, описание, инфо-строки (место / длительность / занятость / цена),
 * MainButton «Записаться» / «Отменить запись» / «В лист ожидания».
 * previewMode — режим предпросмотра из формы (без RSVP-запросов).
 */
function GuestEventDetail({
  event,
  onClose,
  onNotify,
  previewMode = false,
}: {
  event: Event;
  onClose: () => void;
  onNotify: (msg: string, tone?: 'info' | 'error') => void;
  previewMode?: boolean;
}): JSX.Element {
  const status = useRsvpStatus(previewMode ? undefined : event.id);
  const toggle = useToggleRsvp();
  const badge = dateBadge(event.starts_at);
  const cover = coverSrc(event);

  const state = status.data?.state ?? '';
  const going = status.data?.going ?? 0;
  const joined = state === 'going' || state === 'waitlist';
  const isPast = bucketOf(event.starts_at) === 'past';

  const ctaLabel = ((): string => {
    if (toggle.isPending) return 'Сохраняем…';
    if (state === 'going') return 'Отменить запись';
    if (state === 'waitlist') return 'Выйти из листа ожидания';
    if (event.capacity !== undefined && going >= event.capacity) return 'В лист ожидания';
    return 'Записаться';
  })();

  const onToggle = (): void => {
    toggle.mutate(
      { id: event.id, join: !joined },
      {
        onSuccess: (res) => {
          if (!joined) {
            onNotify(
              res.state === 'waitlist'
                ? 'Все места заняты — Вы в листе ожидания. Сообщим, когда место освободится.'
                : `Вы записаны на «${event.title}».`,
            );
          } else {
            onNotify('Запись отменена.');
          }
        },
        onError: (e) => onNotify(e.message || 'Не удалось изменить запись.', 'error'),
      },
    );
  };

  return (
    <section className="events-detail" data-testid="event-detail">
      <div className="ta-topbar">
        <h1 className="hsc-page__title">{previewMode ? 'Предпросмотр события' : 'Событие'}</h1>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="event-detail-close">
          {previewMode ? 'К форме' : 'Назад'}
        </Button>
      </div>

      <Card>
        <div
          className="events-detail__cover"
          style={cover ? { backgroundImage: `url(${cover})` } : undefined}
          data-testid="event-detail-cover"
        >
          <span className="events-detail__cover-badge">
            {badge.day} {badge.month} · {formatTime(event.starts_at)}
          </span>
        </div>
        <h2 className="events-detail__title">{event.title}</h2>
        {event.description && <p className="events-detail__desc">{event.description}</p>}

        <ul className="events-detail__info">
          <li className="events-detail__row">
            <span className="ta-muted">Когда</span>
            <span>
              {formatDateTime(event.starts_at)}
              {event.ends_at ? ` — ${formatTime(event.ends_at)}` : ''}
            </span>
          </li>
          {event.location ? (
            <li className="events-detail__row">
              <span className="ta-muted">Место</span>
              <span data-testid="event-detail-location">{event.location}</span>
            </li>
          ) : null}
          {event.host ? (
            <li className="events-detail__row">
              <span className="ta-muted">Ведущий</span>
              <span data-testid="event-detail-host">{event.host}</span>
            </li>
          ) : null}
          <li className="events-detail__row">
            <span className="ta-muted">Тип</span>
            <span>{EVENT_CATEGORY_LABEL[event.category] ?? event.category}</span>
          </li>
          <li className="events-detail__row">
            <span className="ta-muted">Места</span>
            <span data-testid="event-detail-seats">
              {previewMode ? seatsLabel(0, event.capacity) : status.isLoading ? '…' : seatsLabel(going, event.capacity)}
            </span>
          </li>
          <li className="events-detail__row">
            <span className="ta-muted">Цена</span>
            <span data-testid="event-detail-price">
              {formatPrice(event.price_kopecks, event.price_currency)}
            </span>
          </li>
        </ul>

        {!previewMode && joined && (
          <p className="events-detail__joined" data-testid="event-rsvp-state">
            {state === 'waitlist' ? '⏳ Вы в листе ожидания' : '✓ Вы записаны'}
          </p>
        )}

        {previewMode ? (
          <p className="ta-muted" data-testid="event-preview-note">
            Так гость увидит событие в Telegram после публикации.
          </p>
        ) : isPast ? (
          <p className="ta-muted" data-testid="event-rsvp-past">
            Это мероприятие уже прошло — запись закрыта.
          </p>
        ) : status.isError ? (
          <p role="alert">Не удалось загрузить запись. Попробуйте обновить страницу.</p>
        ) : (
          <Button
            variant={joined ? 'ghost' : 'primary'}
            disabled={toggle.isPending || status.isLoading}
            onClick={onToggle}
            data-testid="event-rsvp-toggle"
          >
            {ctaLabel}
          </Button>
        )}
      </Card>
    </section>
  );
}

export default EventsPage;
